import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useState } from "react";
import { Flex, Text, Button, Grid, Divider, Box, Spinner, Icon, VStack, Image, } from "@chakra-ui/react";
import { useNavigate, useParams } from "react-router-dom";
import { useTables } from "../../hooks/useTables";
import { usePub } from "../../hooks/usePub";
import { useCustomToast } from "../useCustomToast";
import TableFilterDrawer from "./TableFilterDrawer";
import { FiFilter, FiLayout, FiPhone } from "react-icons/fi";
import TableCard from "./TableCard";
import UserSelectionsSummary from "../common/UserSelectionsSummary";
import ChangeSelectionsModal from "../common/ChangeSelectionsModal";
import ResponsiveModal from "../common/ResponsiveModal";
import { IMG_URL } from "../common/Constant";
const TableAreaOld = ({ pubId, time, peopleCount, selectedTable, setSelectedTable, femaleCount = 0, maleCount = 0, timeSlots = [], onTimeChange, }) => {
    const { eventId } = useParams();
    // const toast = useToast();
    const customToast = useCustomToast();
    const navigate = useNavigate();
    const { data, isLoading } = useTables(pubId, eventId, time, maleCount, femaleCount);
    const { data: pub, isLoading: isPubDetailLoading } = usePub(pubId);
    const [availableTables, setAvailableTables] = useState([]);
    const [filteredTables, setFilteredTables] = useState([]);
    const [areaList, setAreaList] = useState([]);
    const [tableTypeList, setTableTypeList] = useState([]);
    const [seats, setSeats] = useState([]);
    const [selectedArea, setSelectedArea] = useState("");
    const [recommendedTables, setRecommendedTables] = useState([]);
    const [otherTables, setOtherTables] = useState([]);
    const [tableLayout, setTableLayout] = useState([]);
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [isChangeModalOpen, setIsChangeModalOpen] = useState(false);
    const defaultPriceRange = [0, 100000];
    const [filters, setFilters] = useState({
        availability: [],
        area: [],
        tableType: [],
        tableFor: [],
        priceRange: defaultPriceRange,
        sortBy: "",
    });
    const openFilter = () => setIsFilterOpen(true);
    const closeFilter = () => setIsFilterOpen(false);
    const openChangeModal = () => setIsChangeModalOpen(true);
    const closeChangeModal = () => {
        setIsChangeModalOpen(false);
    };
    const handleTimeChange = (newTime, newMaleCount, newFemaleCount) => {
        closeChangeModal();
        navigate(-1);
        setTimeout(() => {
            if (onTimeChange) {
                onTimeChange(newTime, newMaleCount, newFemaleCount);
            }
        }, 100);
    };
    // Check if table is disabled due to female requirements
    const isTableDisabled = (table) => {
        return table.minLadiesRequired && femaleCount < table.minLadiesRequired;
    };
    // Check if all tables are sold out
    const allTablesSoldOut = React.useMemo(() => {
        const allTables = [...recommendedTables, ...otherTables];
        return (allTables.length > 0 &&
            allTables.every((table) => table.availableTables === 0));
    }, [recommendedTables, otherTables]);
    // Categorize tables into recommended and other
    const categorizeTables = (tables) => {
        var _a;
        const recommended = [];
        const other = [];
        // Find tables with exact seat count match first
        const exactMatchTables = tables.filter((table) => table.seatCount === peopleCount && !isTableDisabled(table));
        if (exactMatchTables.length > 0) {
            recommended.push(...exactMatchTables);
        }
        else {
            // If no exact match, find the next available seat count
            const sortedTables = tables
                .filter((table) => !isTableDisabled(table))
                .sort((a, b) => a.seatCount - b.seatCount);
            const nextAvailableSeatCount = (_a = sortedTables.find((table) => table.seatCount >= peopleCount)) === null || _a === void 0 ? void 0 : _a.seatCount;
            if (nextAvailableSeatCount) {
                const nextMatchTables = tables.filter((table) => table.seatCount === nextAvailableSeatCount &&
                    !isTableDisabled(table));
                recommended.push(...nextMatchTables);
            }
        }
        // Add all other tables to "other" section
        other.push(...tables.filter((table) => !recommended.includes(table)));
        return { recommended, other };
    };
    // Apply filters to tables
    const applyFilters = (tables, filterState) => {
        let filtered = [...tables];
        // Filter by availability
        if (filterState.availability.length > 0) {
            filtered = filtered.filter((table) => {
                const isFastFilling = table.availableTables > 0;
                const isSoldOut = table.availableTables === 0;
                if (filterState.availability.includes("fast_filling") && isFastFilling)
                    return true;
                if (filterState.availability.includes("sold_out") && isSoldOut)
                    return true;
                return false;
            });
        }
        // Filter by area
        if (filterState.area.length > 0) {
            filtered = filtered.filter((table) => filterState.area.includes(table.areaName));
        }
        // Filter by table type
        if (filterState.tableType.length > 0) {
            filtered = filtered.filter((table) => filterState.tableType.includes(table.tableType));
        }
        // Filter by table for (seat count)
        if (filterState.tableFor.length > 0) {
            filtered = filtered.filter((table) => filterState.tableFor.includes(table.seatCount.toString()));
        }
        // Filter by price range
        if (filterState.priceRange[0] !== 0 ||
            filterState.priceRange[1] !== 100000) {
            filtered = filtered.filter((table) => {
                const price = table.price || 0;
                return (price >= filterState.priceRange[0] &&
                    price <= filterState.priceRange[1]);
            });
        }
        // Sort tables
        if (filterState.sortBy) {
            switch (filterState.sortBy) {
                case "price_low":
                    filtered.sort((a, b) => (a.price || 0) - (b.price || 0));
                    break;
                case "price_high":
                    filtered.sort((a, b) => (b.price || 0) - (a.price || 0));
                    break;
            }
        }
        return filtered;
    };
    // Handle filter apply
    const handleFilterApply = (newFilters) => {
        setFilters(newFilters);
        const filtered = applyFilters(availableTables, newFilters);
        setFilteredTables(filtered);
        // Categorize filtered tables
        const { recommended, other } = categorizeTables(filtered);
        setRecommendedTables(recommended);
        setOtherTables(other);
        closeFilter();
    };
    // Handle clear all filters
    const handleClearFilters = () => {
        const clearedFilters = {
            availability: [],
            area: [],
            tableType: [],
            tableFor: [],
            priceRange: defaultPriceRange,
            sortBy: "",
        };
        setFilters(clearedFilters);
        setFilteredTables(availableTables);
        // Categorize available tables
        const { recommended, other } = categorizeTables(availableTables);
        setRecommendedTables(recommended);
        setOtherTables(other);
        closeFilter();
    };
    useEffect(() => {
        var _a, _b, _c;
        if (!isLoading && ((_c = (_b = (_a = pub === null || pub === void 0 ? void 0 : pub.data) === null || _a === void 0 ? void 0 : _a.pub_detail) === null || _b === void 0 ? void 0 : _b.tableLayout) === null || _c === void 0 ? void 0 : _c.length)) {
            setTableLayout(pub.data.pub_detail.tableLayout);
        }
    }, [isLoading, pub]);
    useEffect(() => {
        var _a, _b, _c, _d, _e;
        if (!isLoading && data && data.data) {
            const maxPrice = ((_a = data.data.tables) === null || _a === void 0 ? void 0 : _a.length)
                ? Math.max(...(_b = data.data.tables) === null || _b === void 0 ? void 0 : _b.map((table) => table.price || 0))
                : 100000;
            setFilters({
                ...filters,
                priceRange: [0, maxPrice],
            });
            const uniqueAreaNames = Array.from(new Set((_c = data.data.tables) === null || _c === void 0 ? void 0 : _c.map((table) => table.areaName)));
            setAreaList(uniqueAreaNames);
            const uniqueTableTypes = Array.from(new Set((_d = data.data.tables) === null || _d === void 0 ? void 0 : _d.map((table) => table.tableType)));
            setTableTypeList(uniqueTableTypes);
            const uniqueSeats = Array.from(new Set((_e = data.data.tables) === null || _e === void 0 ? void 0 : _e.map((table) => table.seatCount))).sort((a, b) => a - b);
            setSeats(uniqueSeats);
            // Set initial available tables
            setAvailableTables(data.data.tables);
            setFilteredTables(data.data.tables);
            // Categorize tables into recommended and other
            const { recommended, other } = categorizeTables(data.data.tables);
            setRecommendedTables(recommended);
            setOtherTables(other);
            // Set default area if available
            if (uniqueAreaNames.length > 0) {
                setSelectedArea(uniqueAreaNames[0]);
            }
        }
        else if ((data === null || data === void 0 ? void 0 : data.status_code) === "FORBIDDEN") {
            customToast({
                title: data.status_message,
                status: "error",
            });
            navigate(-1);
        }
    }, [isLoading, data]);
    // Update filtered tables when filters change
    useEffect(() => {
        const filtered = applyFilters(availableTables, filters);
        setFilteredTables(filtered);
        // Categorize filtered tables
        const { recommended, other } = categorizeTables(filtered);
        setRecommendedTables(recommended);
        setOtherTables(other);
    }, [filters, availableTables]);
    const showTableLayout = () => {
        if (tableLayout.length === 0)
            return;
        navigate("/vibes", {
            state: {
                data: {
                    vibes: {
                        vibes: tableLayout.map((item) => item.imageUrl),
                    },
                },
            },
        });
    };
    if (isLoading || isPubDetailLoading) {
        return (_jsx(Flex, { w: "100%", h: "50vh", justifyContent: "center", alignItems: "center", children: _jsx(Spinner, {}) }));
    }
    return (_jsxs(Flex, { w: "100%", h: "100%", flexDir: "column", position: "relative", children: [time && (_jsx(UserSelectionsSummary, { isNewTableFormat: false, time: time, maleCount: maleCount, femaleCount: femaleCount, onChangeClick: openChangeModal })), allTablesSoldOut ? (_jsxs(Flex, { w: "100%", minH: "30vh", justifyContent: "center", alignItems: "center", flexDir: "column", p: 4, mt: 4, children: [_jsx(Text, { color: "whiteAlpha.800", fontSize: "lg", fontWeight: "600", textAlign: "center", mb: 2, children: "No Tables Available" }), _jsx(Text, { color: "whiteAlpha.600", fontSize: "sm", textAlign: "center", maxW: "400px", children: "All tables are currently sold out. Please try another time slot or contact the venue directly." })] })) : recommendedTables.length > 0 || otherTables.length > 0 ? (_jsxs(Box, { h: "fit-content", mt: 4, children: [recommendedTables.length > 0 && (_jsxs(Box, { mb: 8, children: [_jsx(Text, { fontSize: "xs", fontWeight: "700", color: "white", mb: 4, px: 2, children: "Recommended Tables" }), _jsx(Grid, { templateColumns: "repeat(1, 1fr)", gap: 4, children: recommendedTables
                                    .sort((a, b) => a.seatCount - b.seatCount)
                                    .map((table, index) => table.availableTables || selectedTable === table ? (_jsx(TableCard, { table: table, isSelected: selectedTable === table, isDisabled: isTableDisabled(table), onClick: () => {
                                        setSelectedTable(table);
                                    } }, `recommended-${index}`)) : null) })] })), otherTables.length > 0 && (_jsxs(Box, { children: [_jsx(Text, { fontSize: "xs", fontWeight: "700", color: "white", mb: 4, px: 2, children: "Other Tables" }), _jsx(Grid, { templateColumns: "repeat(1, 1fr)", gap: 4, children: otherTables
                                    .sort((a, b) => a.seatCount - b.seatCount)
                                    .map((table, index) => table.availableTables || selectedTable === table ? (_jsx(TableCard, { table: table, isSelected: selectedTable === table, isDisabled: isTableDisabled(table), onClick: () => {
                                        setSelectedTable(table);
                                    } }, `other-${index}`)) : null) })] }))] })) : (_jsx(Flex, { w: "100%", minH: "30vh", justifyContent: "center", alignItems: "center", flexDir: "column", p: 2, children: pubId.toString() === "866" ? (_jsxs(VStack, { spacing: 6, w: "100%", maxW: "400px", bg: "linear-gradient(135deg, rgba(132, 81, 208, 0.15) 0%, rgba(132, 81, 208, 0.05) 100%)", borderRadius: "20px", border: "1px solid", borderColor: "rgba(132, 81, 208, 0.3)", py: 6, px: 4, boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)", children: [_jsxs(VStack, { spacing: 3, children: [_jsx(Box, { bg: "rgba(132, 81, 208, 0.2)", borderRadius: "full", p: 4, children: _jsx(Image, { src: `${IMG_URL}/icons/table.svg`, w: "40px", h: "40px" }) }), _jsx(Text, { color: "white", fontSize: "md", fontWeight: "600", textAlign: "center", children: "Reserve Your Table" }), _jsx(Text, { color: "whiteAlpha.700", fontSize: "xs", textAlign: "center", lineHeight: "1.6", children: "No tables available online at the moment. Please reach out to the venue directly for reservations." })] }), _jsx(Divider, { borderColor: "whiteAlpha.300" }), _jsxs(VStack, { spacing: 3, w: "100%", children: [_jsx(Text, { color: "whiteAlpha.600", fontSize: "xs", fontWeight: "500", children: "CALL US ON" }), _jsxs(VStack, { spacing: 2.5, w: "100%", children: [_jsx(Button, { as: "a", href: "tel:7788999911", w: "100%", h: "48px", bg: "rgba(132, 81, 208, 0.1)", border: "0.5px solid", borderColor: "rgba(132, 81, 208, 0.4)", borderRadius: "10px", color: "white", fontSize: "sm", fontWeight: "400", leftIcon: _jsx(Icon, { as: FiPhone, boxSize: 3.5 }), _hover: {
                                                bg: "rgba(132, 81, 208, 0.2)",
                                                borderColor: "#8451D0",
                                                transform: "translateY(-1px)",
                                            }, _active: {
                                                bg: "rgba(132, 81, 208, 0.25)",
                                                transform: "translateY(0)",
                                            }, transition: "all 0.2s ease", children: "7788999911" }), _jsx(Button, { as: "a", href: "tel:7788999922", w: "100%", h: "48px", bg: "rgba(132, 81, 208, 0.1)", border: "0.5px solid", borderColor: "rgba(132, 81, 208, 0.4)", borderRadius: "10px", color: "white", fontSize: "sm", fontWeight: "400", leftIcon: _jsx(Icon, { as: FiPhone, boxSize: 3.5 }), _hover: {
                                                bg: "rgba(132, 81, 208, 0.2)",
                                                borderColor: "#8451D0",
                                                transform: "translateY(-1px)",
                                            }, _active: {
                                                bg: "rgba(132, 81, 208, 0.25)",
                                                transform: "translateY(0)",
                                            }, transition: "all 0.2s ease", children: "7788999922" })] })] })] })) : (_jsx(Text, { color: "whiteAlpha.600", fontSize: "sm", p: 4, children: "No tables found matching your criteria" })) })), _jsxs(Flex, { w: (tableLayout === null || tableLayout === void 0 ? void 0 : tableLayout.length) > 0 ? "90%" : "170px", border: "1px solid", borderColor: "#414141", bg: "#232222", borderRadius: 30, h: "60px", position: "fixed", bottom: "20px", right: "20px", align: "center", children: [(tableLayout === null || tableLayout === void 0 ? void 0 : tableLayout.length) > 0 && (_jsx(Button, { w: "50%", h: "100%", leftIcon: _jsx(Icon, { as: FiLayout, boxSize: 6 }), color: "white", borderRadius: "full", bg: "transparent", px: 6, py: 3, fontSize: "xs", fontWeight: 400, _hover: {
                            bg: "transparent",
                        }, _active: {
                            bg: "transparent",
                        }, onClick: showTableLayout, zIndex: 10, children: "Table Layout" })), (tableLayout === null || tableLayout === void 0 ? void 0 : tableLayout.length) > 0 && (_jsx(Divider, { orientation: "vertical", borderColor: "#414141", h: "24px" })), _jsxs(Button, { w: (tableLayout === null || tableLayout === void 0 ? void 0 : tableLayout.length) > 0 ? "50%" : "100%", h: "100%", leftIcon: _jsx(Icon, { as: FiFilter, boxSize: 6 }), color: "white", borderRadius: "full", bg: "transparent", px: 6, py: 3, fontSize: "xs", fontWeight: 400, _hover: {
                            bg: "transparent",
                        }, _active: {
                            bg: "transparent",
                        }, onClick: openFilter, zIndex: 10, children: ["Filters", Object.values(filters).some((value) => {
                                if (Array.isArray(value))
                                    return value.length > 0;
                                if (typeof value === "string")
                                    return value !== "";
                                if (Array.isArray(value) && value.length === 2) {
                                    return value[0] !== 0 || value[1] !== 100000;
                                }
                                return false;
                            }) && (_jsx(Text, { ml: 1, color: "#8451D0", fontSize: "2xl", children: "\u2022" }))] })] }), (availableTables === null || availableTables === void 0 ? void 0 : availableTables.length) > 0 && (_jsx(TableFilterDrawer, { isOpen: isFilterOpen, onClose: closeFilter, tables: availableTables, areas: areaList, tableTypes: tableTypeList, seatCounts: seats, initialFilters: filters, onApply: handleFilterApply, onClearAll: handleClearFilters })), isChangeModalOpen && (_jsx(ResponsiveModal, { isOpen: isChangeModalOpen, onClose: closeChangeModal, paramKey: "table-change-modal", header: "", showCloseButton: false, bg: "#212121", color: "white", size: "md", children: _jsx(ChangeSelectionsModal, { isOpen: isChangeModalOpen, onClose: closeChangeModal, onApply: handleTimeChange, currentTime: time || "", currentMaleCount: maleCount, currentFemaleCount: femaleCount, timeSlots: timeSlots }) }))] }));
};
export default TableAreaOld;
